/**
 * Creator CPV Rate Manager Service
 * Handles database operations for per-creator dynamic CPV rates
 */

import { supabase } from '../lib/supabase';
import { CreatorPerformanceService } from './creatorPerformanceService';

interface CreatorMetrics {
  followers: number;
  totalPosts: number;
  totalLikes: number;
  totalComments: number;
  totalViews: number;
  totalReach: number;
}

export class CreatorCPVRateManager {
  /**
   * Store calculated CPV rate for a creator in a campaign
   */
  static async storeCreatorCPVRate(
    campaignId: string,
    creatorId: string,
    baseCPV: number,
    metrics: CreatorMetrics
  ) {
    try {
      const cpvRate = CreatorPerformanceService.calculateAdjustedCPV(baseCPV, metrics);
      const { performanceScore } = cpvRate;

      const { data, error } = await supabase
        .from('creator_cpv_rates')
        .upsert(
          {
            campaign_id: campaignId,
            creator_id: creatorId,
            base_cpv: baseCPV,
            adjusted_cpv: cpvRate.adjustedCPV,
            performance_score: {
              engagementRate: performanceScore.engagementRate,
              avgViewsPerPost: performanceScore.avgViewsPerPost,
              normalizedFollowers: performanceScore.normalizedFollowers,
              reachRate: performanceScore.reachRate,
              totalScore: performanceScore.totalScore,
              performanceMultiplier: performanceScore.performanceMultiplier
            },
            performance_tier: performanceScore.tier,
            metrics_snapshot: metrics,
            applied_at: new Date().toISOString()
          },
          {
            onConflict: 'campaign_id,creator_id'
          }
        );

      if (error) {
        console.error('Error storing CPV rate:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Exception storing CPV rate:', err);
      return null;
    }
  }

  /**
   * Get effective CPV rate for a creator in a campaign
   */
  static async getEffectiveCPV(campaignId: string, creatorId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('creator_cpv_rates')
        .select('adjusted_cpv')
        .eq('campaign_id', campaignId)
        .eq('creator_id', creatorId)
        .single();

      if (error || !data) {
        console.warn('CPV rate not found, returning 0');
        return 0;
      }

      return data.adjusted_cpv;
    } catch (err) {
      console.error('Error fetching CPV rate:', err);
      return 0;
    }
  }

  /**
   * Get all CPV rates for a campaign
   */
  static async getCampaignCPVRates(campaignId: string) {
    try {
      const { data, error } = await supabase
        .from('creator_cpv_rates')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('adjusted_cpv', { ascending: false });

      if (error) {
        console.error('Error fetching campaign CPV rates:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Exception fetching CPV rates:', err);
      return [];
    }
  }

  /**
   * Get CPV rate details for a creator in a campaign
   */
  static async getCreatorCPVDetails(campaignId: string, creatorId: string) {
    try {
      const { data, error } = await supabase
        .from('creator_cpv_rates')
        .select('*')
        .eq('campaign_id', campaignId)
        .eq('creator_id', creatorId)
        .single();

      if (error) {
        console.error('Error fetching creator CPV details:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Exception fetching CPV details:', err);
      return null;
    }
  }

  /**
   * Calculate projected earnings for a creator with performance CPV
   */
  static async getProjectedEarnings(
    campaignId: string,
    creatorId: string,
    projectedViews: number
  ): Promise<{
    baseCPV: number;
    adjustedCPV: number;
    performanceMultiplier: number;
    baseEarnings: number;
    adjustedEarnings: number;
    earningsBoost: number;
    performanceTier: string;
  } | null> {
    try {
      const cpvData = await this.getCreatorCPVDetails(campaignId, creatorId);

      if (!cpvData) {
        return null;
      }

      const baseEarnings = cpvData.base_cpv * projectedViews;
      const adjustedEarnings = cpvData.adjusted_cpv * projectedViews;
      const boost = adjustedEarnings - baseEarnings;
      const multiplier =
        cpvData.performance_score?.performanceMultiplier || cpvData.adjusted_cpv / cpvData.base_cpv;

      return {
        baseCPV: parseFloat(cpvData.base_cpv),
        adjustedCPV: parseFloat(cpvData.adjusted_cpv),
        performanceMultiplier: parseFloat(multiplier.toFixed(3)),
        baseEarnings: parseFloat(baseEarnings.toFixed(2)),
        adjustedEarnings: parseFloat(adjustedEarnings.toFixed(2)),
        earningsBoost: parseFloat(boost.toFixed(2)),
        performanceTier: cpvData.performance_tier
      };
    } catch (err) {
      console.error('Error calculating projected earnings:', err);
      return null;
    }
  }

  /**
   * Bulk update CPV rates for multiple creators in a campaign
   */
  static async bulkUpdateCreatorCPVRates(
    campaignId: string,
    creators: Array<{
      creatorId: string;
      baseCPV: number;
      metrics: CreatorMetrics;
    }>
  ) {
    try {
      const rates = creators.map(creator => {
        const cpvRate = CreatorPerformanceService.calculateAdjustedCPV(
          creator.baseCPV,
          creator.metrics
        );
        return {
          campaign_id: campaignId,
          creator_id: creator.creatorId,
          base_cpv: creator.baseCPV,
          adjusted_cpv: cpvRate.adjustedCPV,
          performance_score: {
            engagementRate: cpvRate.performanceScore.engagementRate,
            avgViewsPerPost: cpvRate.performanceScore.avgViewsPerPost,
            normalizedFollowers: cpvRate.performanceScore.normalizedFollowers,
            reachRate: cpvRate.performanceScore.reachRate,
            totalScore: cpvRate.performanceScore.totalScore,
            performanceMultiplier: cpvRate.performanceScore.performanceMultiplier
          },
          performance_tier: cpvRate.performanceScore.tier,
          metrics_snapshot: creator.metrics,
          applied_at: new Date().toISOString()
        };
      });

      const { data, error } = await supabase.from('creator_cpv_rates').upsert(rates);

      if (error) {
        console.error('Error bulk updating CPV rates:', error);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Exception bulk updating CPV rates:', err);
      return null;
    }
  }

  /**
   * Get CPV statistics for a campaign
   */
  static async getCampaignCPVStats(campaignId: string) {
    try {
      const { data, error } = await supabase
        .from('creator_cpv_rates')
        .select('base_cpv, adjusted_cpv, performance_tier')
        .eq('campaign_id', campaignId);

      if (error || !data) {
        return null;
      }

      const baseCPVs = data.map(d => d.base_cpv);
      const adjustedCPVs = data.map(d => d.adjusted_cpv);

      const avgBaseCPV = baseCPVs.reduce((a, b) => a + b, 0) / baseCPVs.length;
      const avgAdjustedCPV = adjustedCPVs.reduce((a, b) => a + b, 0) / adjustedCPVs.length;
      const maxAdjustedCPV = Math.max(...adjustedCPVs);
      const minAdjustedCPV = Math.min(...adjustedCPVs);

      const tierBreakdown = data.reduce((acc, d) => {
        acc[d.performance_tier] = (acc[d.performance_tier] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return {
        totalCreators: data.length,
        avgBaseCPV: parseFloat(avgBaseCPV.toFixed(4)),
        avgAdjustedCPV: parseFloat(avgAdjustedCPV.toFixed(4)),
        minAdjustedCPV: parseFloat(minAdjustedCPV.toFixed(4)),
        maxAdjustedCPV: parseFloat(maxAdjustedCPV.toFixed(4)),
        cpvRange: parseFloat((maxAdjustedCPV - minAdjustedCPV).toFixed(4)),
        tierBreakdown
      };
    } catch (err) {
      console.error('Error getting CPV stats:', err);
      return null;
    }
  }
}

export default CreatorCPVRateManager;
